/* commin imports */

import { ObjectId } from "bson";
import { useRouter } from "next/router";
import React, { useState } from "react";

/* mui imports */

import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import {
	Alert,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Slide,
	Snackbar,
	styled,
	Tab,
	Tabs,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";

/* models import */

import { BusinessProfileInterface, PropertyInterface, UserInterface } from "../../models";

/* styled components */

const CustomDialog = styled(Dialog)(({ theme }) => ({
	"& .MuiDialog-paper": {
		[theme.breakpoints.down("sm")]: {
			borderRadius: "0rem",
			minHeight: "100%",
		},
		minHeight: "calc(100vh - 100px)",
		maxHeight: "calc(100vh - 100px)",
		borderRadius: "1rem",
		background: theme.palette.background.paper,
	},
}));

const CustomDialogTitle = styled(DialogTitle)(({ theme }) => ({
	display: "flex",
	flsexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	padding: "0rem 1rem",
}));

const GridContainer = styled("div")(({ theme }) => ({
	display: "grid",
	justifyContent: "center",
	alignItems: "center",
	gridTemplateColumns: "repeat(2, 1fr)",
	gap: "10px",
}));

const ContentContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	height: "100%",
}));

const Images = styled("img")(({ theme }) => ({
	width: "100%",
	height: "auto",
	maxHeight: "calc(100vh - 200px)",
	objectFit: "contain",
	cursor: "pointer",
}));

const Snack = styled(Snackbar)(({ theme }) => ({}));

const BottomArrowContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	position: "absolute",
	bottom: "20px",
	left: "50%",
	transform: "translateX(-50%)",
	gap: "0.5rem",
	background: "transparent",
	backgroundColor: "rgba(63, 55, 55, 0.5)",
	borderRadius: "1rem",
	padding: "0.25rem 0.5rem",
	width: "15rem",
	height: "3rem",
	[theme.breakpoints.down("sm")]: {
		width: "18.75rem",
		padding: "0.25rem 0.5rem",
		backgroundColor: "rgba(63, 55, 55, 0.5)",
		height: "auto",
		gap: "0.25rem",
	},
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
	width: "fit-content",
	alignSelf: "start",
	borderRadius: "8px",
	"& .MuiTabs-root > .MuiTabs-scroller > .MuiTabs-indicator": {
		display: "none",
	},
	minHeight: "0rem",
	height: "auto",
})) as typeof Tabs;

const StyledTab = styled(Tab)(({ theme, label }) => ({
	textTransform: "none",
	fontSize: "0.875rem",
	fontWeight: 500,
	gap: "0.5rem",
	letterSpacing: "0.025rem",
	background: "transparent",
	"&.Mui-selected": {
		background: " rgba(252, 128, 25, 0.16)",
		color: "#B05911",
		borderRadius: "1rem",
		border: "2px solid #B05911",
	},
})) as typeof Tab;

const TextOverlayContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "center",
	position: "absolute",
	bottom: "2px",
	right: "0px",
	zIndex: 2,
	backgroundColor: "rgba(0, 0, 0, 0.5)",
	padding: "5px 10px",
	// borderRadius: "1rem 0rem 0rem 0rem",
	// border: "1px solid #FFFFFF",
	cursor: "pointer",
	gap: "0.5rem",
	"&:hover": {
		opacity: 0.9,
	},
}));

const ButtonsContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	gap: "0.25rem",
	padding: "0rem 0.25rem 0rem 0.25rem",
}));

/* Dialog Transition */

const DialogTransition = React.forwardRef(function Transition(
	props: TransitionProps & {
		children: React.ReactElement<any, any>;
	},
	ref: React.Ref<unknown>,
) {
	return (
		<Slide
			direction="up"
			ref={ref}
			{...props}
		/>
	);
});

/* types */

type Category = "all" | "exterior" | "interior" | "master_plan" | "amenities" | "route_map";

/* interface */
interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

const ViewChatImagesModal = ({
	open,
	allImages,
	handleClose,
}: {
	open: boolean;
	allImages: Array<string>;
	handleClose: () => void;
}) => {
	const router = React.useRef(useRouter()).current;

	const theme = useTheme();

	const [openSingleViewModal, setOpenSingleViewModal] = useState<boolean>(false);

	const [selectedTab, setSelectedTab] = useState<Category>("all");

	const [selectedImage, setSelectedImage] = useState<string | null>(null);

	const [touchStartX, setTouchStartX] = useState<number | null>(null);

	const [touchEndX, setTouchEndX] = useState<number | null>(null);

	const [touchStartY, setTouchStartY] = useState<number | null>(null);

	const [touchEndY, setTouchEndY] = useState<number | null>(null);

	const [snackopen, setSnackOpen] = React.useState<boolean>(false);

	const [snackBarMessage, setSnackBarMessage] = React.useState<string>("");

	const EDGE_SWIPE_THRESHOLD = 50; // Pixels from the screen edge to detect edge swipes
	const SWIPE_DOWN_THRESHOLD = 50; // Minimum swipe distance to trigger modal close

	// const minSwipeDistance = 50;

	const smDevice = useMediaQuery(theme.breakpoints.down("sm"));

	const isDesktop = useMediaQuery(theme.breakpoints.down("md1190"));

	const handleImageClick = (image: string) => {
		setSelectedImage(image);
		setOpenSingleViewModal(true);
	};

	const handleNextImage = () => {
		const currentIndex = allImages.indexOf(selectedImage!);
		const nextIndex = (currentIndex + 1) % allImages.length;
		setSelectedImage(allImages[nextIndex]);
	};

	const handlePrevImage = () => {
		const currentIndex = allImages.indexOf(selectedImage!);
		const prevIndex = (currentIndex - 1 + allImages.length) % allImages.length;
		setSelectedImage(allImages[prevIndex]);
	};

	const handleCloseSnack = () => {
		setSnackOpen(false);
	};

	const minSwipeDistance = 50; // Minimum swipe distance to trigger an action
	const edgeSwipeThreshold = 50; // Edge detection threshold

	function onTouchStart(e: React.TouchEvent) {
		setTouchEndX(null);
		setTouchStartX(e.targetTouches[0].clientX);

		setTouchEndY(null);
		setTouchStartY(e.targetTouches[0].clientY);
	}

	function onTouchMove(e: React.TouchEvent) {
		setTouchEndX(e.targetTouches[0].clientX);
		setTouchEndY(e.targetTouches[0].clientY);
	}

	function onTouchEnd() {
		if (touchStartX !== null && touchEndX !== null && touchStartY !== null && touchEndY !== null) {
			const xDistance = touchStartX - touchEndX;
			const yDistance = touchStartY - touchEndY;

			// const isSwipeDown = yDistance < -minSwipeDistance;
			const isEdgeSwipe = touchStartX < edgeSwipeThreshold || touchStartX > window.innerWidth - edgeSwipeThreshold;

			// Close modal if user swipes from the edge or swipes downward
			if (isEdgeSwipe) {
				openSingleViewModal ? setOpenSingleViewModal(false) : handleClose();
				return; // Exit to prevent further execution
			}

			// Handle left/right swipes for image navigation
			const isLeftSwipe = xDistance > minSwipeDistance;
			const isRightSwipe = xDistance < -minSwipeDistance;

			if (isLeftSwipe) {
				handleNextImage();
			}

			if (isRightSwipe) {
				handlePrevImage();
			}
		}
	}

	React.useEffect(() => {
		if (allImages.length === 1 && open) {
			setSelectedImage(allImages[0]);
			setOpenSingleViewModal(true);
			handleClose();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allImages, open]);

	return (
		<React.Fragment>
			<Snack
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				open={snackopen}
				autoHideDuration={3000}
				onClose={handleCloseSnack}
			>
				<Alert
					onClose={handleCloseSnack}
					severity="success"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
					variant="filled"
				>
					{snackBarMessage}
				</Alert>
			</Snack>
			<CustomDialog
				fullScreen={smDevice}
				open={open}
				maxWidth="md"
				fullWidth
				TransitionComponent={DialogTransition}
			>
				<CustomDialogTitle
					sx={{
						padding: "1rem",
						display: "flex",
						flexDirection: "row",
						justifyContent: "flex-end",
						alignItems: "flex-end",
					}}
				>
					<IconButton
						edge="end"
						color="inherit"
						onClick={handleClose}
						aria-label="close"
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</CustomDialogTitle>
				<DialogContent
					onTouchStart={onTouchStart}
					onTouchMove={onTouchMove}
					onTouchEnd={onTouchEnd}
					sx={{ padding: "0rem 0.5rem", scrollbarWidth: "none" }}
				>
					{selectedTab === "all" && (
						<div
							style={{
								columnCount: smDevice ? 1 : 2,
								columnGap: "1rem",
							}}
						>
							{allImages.map((image, index) => {
								return (
									<div
										key={index}
										style={{
											breakInside: "avoid",
											marginBottom: "1rem",
											position: "relative", // needed for absolute child
											borderRadius: "8px",
											overflow: "hidden",
										}}
									>
										<Images
											src={image}
											alt={`Image ${index + 1}`}
											onClick={() => handleImageClick(image)}
											style={{ width: "100%", display: "block" }}
										/>
									</div>
								);
							})}
						</div>
					)}
				</DialogContent>
				<DialogActions
					sx={{
						padding: "1rem",
						display: "flex",
						flexDirection: "row",
						justifyContent: smDevice ? "space-between" : "flex-end",
					}}
				></DialogActions>
			</CustomDialog>
			<Dialog
				open={openSingleViewModal}
				onClose={() => setOpenSingleViewModal(false)}
				maxWidth="xl"
				fullWidth
				TransitionComponent={DialogTransition}
				sx={{
					"& .MuiDialog-paper": {
						margin: 0,
						width: "100%",
						height: "100%",
						maxWidth: "100%",
						maxHeight: "100%",
						borderRadius: 0,
						backgroundColor: "rgba(0, 0, 0, 0.9)",
					},
				}}
			>
				<DialogTitle>
					<IconButton
						edge="end"
						// color="primary"
						onClick={() => setOpenSingleViewModal(false)}
						aria-label="close"
						sx={{
							position: "absolute",
							top: "10px",
							right: "20px",
							transform: "scale(1)",
							zIndex: 1,
							// backgroundColor: "transparent",
							color: "#FFFFFF",
						}}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>

				<DialogContent
					onTouchStart={onTouchStart}
					onTouchMove={onTouchMove}
					onTouchEnd={onTouchEnd}
					sx={{ textAlign: "center", padding: "0rem 0rem 2rem 0rem", overflow: "hidden" }}
				>
					{smDevice ? (
						<>
							{selectedImage && (
								<>
									<img
										src={selectedImage}
										alt="Selected Property Image"
										style={{ width: "100%", height: "100%", objectFit: "contain", position: "relative", zIndex: 0 }}
									/>
									{allImages.length > 1 && (
										<BottomArrowContainer>
											<IconButton
												onClick={handlePrevImage}
												sx={{
													color: "#FFFFFF",
												}}
											>
												<ArrowForwardIosIcon
													sx={{
														height: "2rem",
														width: "2rem",
														transform: "rotate(180deg)",
													}}
												/>
											</IconButton>
											<div
												style={{
													display: "flex",
													flexDirection: "column",
													justifyContent: "center",
													alignItems: "center",
												}}
											>
												{/* {selectedImage && (
													<Typography sx={{ color: "#FFF", fontSize: "1rem", fontWeight: 400 }}>
														{Object.keys(allImageUrlsWithCategory)
															.find((category) =>
																allImageUrlsWithCategory[category as Category].includes(selectedImage),
															)
															?.replace(/_/g, " ")
															?.replace(/^\w/, (c) => c.toUpperCase())}
													</Typography>
												)} */}
												<Typography
													variant="body1"
													sx={{ color: "#FFF", fontSize: "1rem", fontWeight: 400 }}
												>
													{allImages.indexOf(selectedImage!) + 1} / {allImages.length}
												</Typography>
											</div>
											<IconButton
												onClick={handleNextImage}
												sx={{
													color: "#FFFFFF",
												}}
											>
												<ArrowForwardIosIcon
													sx={{
														height: "2rem",
														width: "2rem",
													}}
												/>
											</IconButton>
										</BottomArrowContainer>
									)}
								</>
							)}
						</>
					) : (
						<div
							style={{
								display: "flex",
								flexDirection: "row",
								justifyContent: "center",
								alignItems: "center",
								width: "100%",
								height: "100%",
							}}
						>
							<img
								src={selectedImage || ""}
								alt="Selected Property Image"
								style={{ width: "100%", height: "100%", objectFit: "contain", position: "relative", zIndex: 0 }}
							/>
							{allImages.length > 1 && (
								<IconButton
									onClick={handlePrevImage}
									sx={{
										color: "#FFFFFF",
										position: "absolute",
										top: "50%",
										left: "20px",
									}}
								>
									<ArrowForwardIosIcon
										sx={{
											height: "2rem",
											width: "2rem",
											transform: "rotate(180deg)",
										}}
									/>
								</IconButton>
							)}
							{allImages.length > 1 && (
								<IconButton
									onClick={handleNextImage}
									sx={{
										color: "#FFFFFF",
										position: "absolute",
										top: "50%",
										right: "20px",
									}}
								>
									<ArrowForwardIosIcon
										sx={{
											height: "2rem",
											width: "2rem",
										}}
									/>
								</IconButton>
							)}
						</div>
					)}
				</DialogContent>
			</Dialog>
		</React.Fragment>
	);
};

export default ViewChatImagesModal;
